import { useCallback, useState } from "react";
import { BACKEND_API_BASE } from "../env";
import { acquireToken } from "../auth/msalClient";

type LoadState = "idle" | "loading" | "ready" | "error";
type TokenKind = "delegated" | "app";

type DevToolsPanelProps = {
  open: boolean;
  onClose: () => void;
};

async function readJson<T>(response: Response): Promise<T> {
  return (await response.json().catch(() => ({}))) as T;
}

export default function DevToolsPanel({ open, onClose }: DevToolsPanelProps) {
  const [token, setToken] = useState("");
  const [tokenKind, setTokenKind] = useState<TokenKind>("delegated");
  const [status, setStatus] = useState<LoadState>("idle");
  const [message, setMessage] = useState("");

  const fetchToken = useCallback(async (kind: TokenKind) => {
    setStatus("loading");
    setMessage("");
    setToken("");
    setTokenKind(kind);

    try {
      const backendToken = await acquireToken();
      const path = kind === "delegated" ? "/api/dev/obo-token" : "/api/dev/app-token";
      const response = await fetch(`${BACKEND_API_BASE}${path}`, {
        method: "POST",
        cache: "no-store",
        headers: {
          Authorization: `Bearer ${backendToken}`,
          "Content-Type": "application/json",
        },
      });
      const data = await readJson<{ graphToken?: string; error?: string }>(response);
      if (!response.ok || !data.graphToken) {
        throw new Error(
          data.error ??
            `${kind === "delegated" ? "Delegated" : "App-only"} token request failed (${response.status})`,
        );
      }
      setToken(data.graphToken);
      setStatus("ready");
    } catch (err) {
      setStatus("error");
      setMessage(
        err instanceof Error
          ? err.message
          : `Failed to get ${kind === "delegated" ? "delegated" : "app-only"} token`,
      );
    }
  }, []);

  const copyToken = useCallback(async () => {
    if (!token) return;
    const tokenLabel = tokenKind === "delegated" ? "delegated" : "app-only";
    try {
      await navigator.clipboard.writeText(token);
      setStatus("ready");
      setMessage(`Copied ${tokenLabel} Graph token.`);
    } catch {
      setStatus("error");
      setMessage(`Failed to copy ${tokenLabel} Graph token.`);
    }
  }, [token, tokenKind]);

  if (!open) return null;

  return (
    <div className="exms-devtools" data-testid="devtools">
      <section className="exms-devtools__panel" aria-label="Developer tools">
        <div className="exms-devtools__header">
          <strong>Cloud storage diagnostics</strong>
          <button type="button" onClick={onClose} aria-label="Close dev tools">
            x
          </button>
        </div>
        <p>
          Get a delegated Graph token for app-owned cloud storage probes, or an app-only token for
          backend provisioning diagnostics.
        </p>
        <div className="exms-devtools__actions">
          <button
            type="button"
            onClick={() => fetchToken("delegated")}
            disabled={status === "loading"}
          >
            {status === "loading" && tokenKind === "delegated"
              ? "Getting delegated token..."
              : "Get delegated token"}
          </button>
          <button type="button" onClick={() => fetchToken("app")} disabled={status === "loading"}>
            {status === "loading" && tokenKind === "app" ? "Getting app token..." : "Get app token"}
          </button>
        </div>
        {token ? (
          <>
            <textarea
              className="exms-devtools__token"
              readOnly
              value={token}
              aria-label={
                tokenKind === "delegated" ? "Delegated Graph token" : "App-only Graph token"
              }
            />
            <button type="button" onClick={copyToken}>
              Copy {tokenKind === "delegated" ? "delegated" : "app"} token
            </button>
          </>
        ) : null}
        {status === "error" || message ? (
          <div className="exms-devtools__message" role={status === "error" ? "alert" : "status"}>
            {message}
          </div>
        ) : null}
      </section>
    </div>
  );
}
